import { DateTime } from 'luxon';
import type { GroupChannel } from '@sendbird/chat/groupChannel';

import { AvatarProps } from '@equip.health/ui';

import { TaskCardProps } from '~/components/common/Tasks';
import { getUnreadMessagesCount } from '~/lib/util/chat/chat.utils';
import { getPreferredFullName } from '~/lib/util/patient.util';
import { CompServTask } from '~/lib/util/task.util';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';

import { APPOINTMENTS_LOOKBACK_MINUTES } from './rightRail.constants';

export const mapCompServTaskToTaskCardProps = (
  task: CompServTask,
): TaskCardProps => {
  const { assignee, familyAssignees, isFamilyTask } = task;
  const providerAssignees: AvatarProps[] = assignee
    ? [
        {
          name: getDisplayNameOrLegalNameForProvider(
            assignee.displayFirstName,
            assignee.firstName,
            assignee.displayLastName,
            assignee.lastName,
          ),
          src: assignee.avatarDetails?.url ?? assignee.avatarUrl,
          userType: 'provider',
        },
      ]
    : [];
  // A family task's provider assignee comes back empty; its people are in familyAssignees.
  const assignees: AvatarProps[] = isFamilyTask
    ? (familyAssignees ?? []).map(
        ({ chosenName, firstName, lastName, userType }) => ({
          name: getPreferredFullName(chosenName, firstName, lastName),
          userType,
        }),
      )
    : providerAssignees;

  return {
    assignees,
    category: task.category?.categoryName ?? '',
    id: task.taskExternalId,
    status: task.status?.taskStatusName ?? '',
    subCategory: task.subCategory?.categoryName,
    title: task.taskName,
  };
};

export const getUpcomingAppointmentsStartISO = (now: DateTime): string =>
  now.minus({ minutes: APPOINTMENTS_LOOKBACK_MINUTES }).toISO();

export const isUpcoming = (
  item: { endDateTime: string },
  now: DateTime,
): boolean => DateTime.fromISO(item.endDateTime) > now;

// Rotated-channel REST fallbacks carry no unread count, so the SDK getter yields
// NaN for them, and one such channel would poison the badge's sum.
const unreadCount = (channel: GroupChannel): number =>
  getUnreadMessagesCount(channel) || 0;

export const getTotalUnread = (channels: GroupChannel[]): number =>
  channels.reduce((sum, channel) => sum + unreadCount(channel), 0);

export const sortUnreadChannelsByRecent = (
  channels: GroupChannel[],
): GroupChannel[] =>
  channels
    .filter((channel) => unreadCount(channel) > 0)
    .sort(
      (a, b) =>
        (b.lastMessage?.createdAt ?? 0) - (a.lastMessage?.createdAt ?? 0),
    );
